import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TreeTable, TreeTableExpandedKeysType } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { TreeNode } from 'primereact/treenode';
import { ETypeOfNotification, InputDate, setNotification } from 'medere-ui-framework';
import {
    MAILING_REPORT_LEVEL_CONFIGURATION,
    MailingReportDateRange,
    MailingReportNodeDTO,
} from '../../dto/MailingReportDTO';
import { getMailingReportSummary } from '../../services/MailingReportController';
import { parseApiError } from '../../services/apiError';
import { toISOString } from '../../utils/localeHelpers';
import MailingReportDetailModal, { configurationLabel } from './MailingReportDetailModal';
import './MailingReportView.scss';

function toDateRange(from: Date, to: Date): MailingReportDateRange {
    return {
        initialDate: toISOString(from)!.substring(0, 10),
        finalDate: toISOString(to)!.substring(0, 10),
    };
}

function toTreeNodes(nodes: MailingReportNodeDTO[]): TreeNode[] {
    return nodes.map(node => ({
        key: node.key,
        data: node,
        leaf: node.children.length === 0,
        children: toTreeNodes(node.children),
    }));
}

/** Camino desde la configuración hasta el nodo, para armar el título del detalle. */
function findPath(nodes: MailingReportNodeDTO[], key: string): MailingReportNodeDTO[] {
    for (const node of nodes) {
        if (node.key === key) return [node];
        const childPath = findPath(node.children, key);
        if (childPath.length > 0) return [node, ...childPath];
    }
    return [];
}

function nodeLabel(node: MailingReportNodeDTO): string {
    return node.level === MAILING_REPORT_LEVEL_CONFIGURATION
        ? configurationLabel(node.privateConfiguration)
        : node.description || '—';
}

function MailingReportView() {
    const dispatch = useDispatch();
    const today = new Date();
    const [fromDate, setFromDate] = useState<Date>(today);
    const [toDate, setToDate] = useState<Date>(today);
    const [range, setRange] = useState<MailingReportDateRange>(toDateRange(today, today));
    const [nodes, setNodes] = useState<MailingReportNodeDTO[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<TreeTableExpandedKeysType>({});
    const [loading, setLoading] = useState(false);
    const [dateError, setDateError] = useState<string | null>(null);
    const [selectedNode, setSelectedNode] = useState<MailingReportNodeDTO | null>(null);

    const treeNodes = useMemo(() => toTreeNodes(nodes), [nodes]);
    const selectedPath = useMemo(() => selectedNode ? findPath(nodes, selectedNode.key) : [], [nodes, selectedNode]);

    async function loadSummary(requestedRange: MailingReportDateRange) {
        setLoading(true);
        try {
            const summary = await getMailingReportSummary(requestedRange);
            setNodes(summary);
            setRange(requestedRange);
            // Igual que en la versión Vaadin: las configuraciones abiertas, mostrando los tipos de mensaje.
            setExpandedKeys(summary.reduce((acc, node) => ({ ...acc, [node.key]: true }), {}));
        } catch (e) {
            setNodes([]);
            dispatch(setNotification({
                title: 'No se pudo cargar el reporte de mensajería',
                desc: parseApiError(e).message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadSummary(range);
    }, []);

    function handleConsult() {
        if (fromDate > toDate) {
            setDateError('Ingresá una fecha hasta igual o posterior a la fecha desde');
            return;
        }
        setDateError(null);
        loadSummary(toDateRange(fromDate, toDate));
    }

    const count = (field: 'countCreated' | 'countSent' | 'countCancelled') =>
        (node: TreeNode) => (node.data as MailingReportNodeDTO)[field];

    return (
        <div className="mailing-report viewContainer bg-white">
            <div className="mailing-report__header-row">
                <h1 className="mailing-report__title">Reporte de mensajería</h1>
            </div>

            <div className="mailing-report__filters">
                <div className="mailing-report__field">
                    <InputDate label="Desde" value={fromDate} maxDate={today}
                        onChange={(e: any) => { setFromDate(e.value ?? today); setDateError(null); }} />
                </div>
                <div className="mailing-report__field">
                    <InputDate label="Hasta" value={toDate} maxDate={today}
                        className={dateError ? 'p-invalid' : ''}
                        onChange={(e: any) => { setToDate(e.value ?? today); setDateError(null); }} />
                    {dateError && (
                        <small className="p-error mailing-report__field-error" role="alert">
                            <i className="pi pi-exclamation-triangle" aria-hidden /> {dateError}
                        </small>
                    )}
                </div>
                {/* Mismo contenedor y label (invisible) que InputDate, para que el botón quede a la altura del input. */}
                <div className="inputContainer flexible--column mailing-report__action-field">
                    <p className="label inputLabel mailing-report__ghost-label" aria-hidden="true">&nbsp;</p>
                    <Button className="buttonMain mailing-report__consult" label="Consultar" loading={loading} onClick={handleConsult} />
                </div>
            </div>

            <TreeTable
                value={treeNodes}
                expandedKeys={expandedKeys}
                onToggle={e => setExpandedKeys(e.value)}
                loading={loading}
                className="mailing-report__table"
                rowClassName={(node: TreeNode) => ({
                    'mailing-report__row-configuration': (node.data as MailingReportNodeDTO).level === MAILING_REPORT_LEVEL_CONFIGURATION,
                })}
                emptyMessage="No hay mensajes para este rango de fechas."
            >
                <Column expander header="Configuración / tipo de mensaje / tipo de tarea"
                    body={(node: TreeNode) => nodeLabel(node.data as MailingReportNodeDTO)} />
                <Column header="Pendientes de envío" body={count('countCreated')}
                    className="mailing-report__cell-center" headerClassName="mailing-report__cell-center" />
                <Column header="Enviados" body={count('countSent')}
                    className="mailing-report__cell-center" headerClassName="mailing-report__cell-center" />
                <Column header="Cancelados" body={count('countCancelled')}
                    className="mailing-report__cell-center" headerClassName="mailing-report__cell-center" />
                <Column className="mailing-report__cell-action" body={(node: TreeNode) => (
                    <Button text className="nv-icon" icon="pi pi-eye" aria-label="Ver detalle"
                        tooltip="Ver detalle" tooltipOptions={{ event: 'both', position: 'left' }}
                        onClick={() => setSelectedNode(node.data as MailingReportNodeDTO)} />
                )} />
            </TreeTable>

            <MailingReportDetailModal
                node={selectedNode}
                path={selectedPath}
                range={range}
                onHide={() => setSelectedNode(null)}
            />
        </div>
    );
}

export default MailingReportView;
